/**
 * SearchBar - Core Reusable Search Component
 * 
 * This is a SCORABLE component - theme-agnostic and reusable across all themes.
 * Provides search functionality for any resource type.
 */

'use client';

import { useEffect, useState } from 'react';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';

export interface SearchBarProps {
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Current search value
   */
  value?: string;
  /**
   * Callback when search changes
   */
  onChange?: (value: string) => void;
  /**
   * Callback when search is submitted
   */
  onSubmit?: (value: string) => void;
  /**
   * Debounce delay in milliseconds
   */
  debounceMs?: number;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Show search icon
   */
  showIcon?: boolean;
  /**
   * Clearable search
   */
  clearable?: boolean;
}

/**
 * Core Search Bar Component
 * 
 * Provides search input with debouncing and optional clearing.
 * Themes can customize appearance via className overrides.
 */
export function SearchBar({
  placeholder = 'Search...',
  value: controlledValue,
  onChange,
  onSubmit,
  debounceMs = 300,
  className = '',
  showIcon = true,
  clearable = true,
}: SearchBarProps) {
  const [internalValue, setInternalValue] = useState('');
  const [localInput, setLocalInput] = useState(controlledValue || '');
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  // Sync local input with controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setLocalInput(controlledValue);
    }
  }, [controlledValue]);

  // Debounce search input changes
  useEffect(() => {
    if (debounceMs === 0) return;
    
    const timer = setTimeout(() => {
      if (localInput !== value) {
        if (controlledValue === undefined) {
          setInternalValue(localInput);
          onChange?.(localInput);
        }
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localInput, debounceMs, value, controlledValue, onChange]);

  const handleChange = (newValue: string) => {
    setLocalInput(newValue);
    
    // If debouncing is disabled, call onChange immediately
    if (debounceMs === 0) {
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    } else if (controlledValue !== undefined) {
      // For controlled components, call onChange immediately (parent handles state)
      onChange?.(newValue);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit?.(value);
    if (value.trim()) {
      analyticsEvents.search(value.trim());
    }
  };

  const handleClear = () => {
    setLocalInput('');
    if (controlledValue === undefined) {
      setInternalValue('');
    }
    onChange?.('');
  };

  return (
    <form onSubmit={handleSubmit} className={`relative ${className}`}>
      <div className="relative">
        {showIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg
              className="h-5 w-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        )}
        
        <input
          type="text"
          value={localInput}
          onChange={(e) => handleChange(e.target.value)}
          placeholder={placeholder}
          className={`
            block w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-10
            text-sm placeholder-gray-500 focus:border-primary focus:outline-none 
            focus:ring-1 focus:ring-primary
            ${showIcon ? 'pl-10' : 'pl-3'}
            ${clearable && value ? 'pr-10' : ''}
          `}
        />
        
        {clearable && localInput && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    </form>
  );
}

